import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CurrentUserService } from 'src/app/user/services';
import { DocumentRetention } from '../models/document-retention';
import { MaintenanceService } from '../services/maintenance.service';
import { DocumentTypeEntity } from 'src/app/distribution/models';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { DeveloperService } from 'src/app/developer/services/developer.service';
import { on } from 'devextreme/events';
import { DxDataGridComponent, DxTooltipComponent, DxDataGridModule, DxDropDownBoxModule, DxTooltipModule } from 'devextreme-angular';
import { DxiItemComponent, DxoEditingModule, DxoPopupModule, DxoFormModule, DxiItemModule, DxiColumnModule, DxoLookupModule, DxiValidationRuleModule, DxiButtonModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import dxDataGrid from 'devextreme/ui/data_grid';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxTemplateModule } from 'devextreme-angular/core';


@Component({
    templateUrl: './document-retention.component.html',
    styleUrls: ['./document-retention.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoPopupModule, DxoFormModule, DxiItemModule, DxiColumnModule, DxoLookupModule, DxiValidationRuleModule, DxiButtonModule, DxTemplateModule, DxDropDownBoxModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule, DxTooltipModule]
})
export class DocumentRetentionComponent implements OnInit {
  @ViewChild(DxTooltipComponent) tooltip: DxTooltipComponent;
  @ViewChild(DxDataGridComponent) grid: DxDataGridComponent;

  public toolTipText = '';
  public allowUpdating = false;
  public allowAdding = false;
  public allowDeleting = false;
  public allowShowLog = false;

  public documentRetentions: DocumentRetention[] = [];
  public documentTypes: DocumentTypeEntity[] = [];
  public availableDocumentTypes: DocumentTypeEntity[] = [];
  public availableDocumentTypesForEditing: DocumentTypeEntity[] = [];

  constructor(
    private currentUserService: CurrentUserService,
    private maintenanceService: MaintenanceService,
    private developerService: DeveloperService,
    private loadingPanelService: LoadingPanelService,
    private toastService: ToastService,
    private gridService: GridService) {
  }

  async ngOnInit(): Promise<void> {
    this.allowAdding = this.currentUserService.hasPermission('Maintenance_Action_DocumentRetentions_Create');
    this.allowUpdating = this.currentUserService.hasPermission('Maintenance_Action_DocumentRetentions_Edit');
    this.allowDeleting = this.currentUserService.hasPermission('Maintenance_Action_DocumentRetentions_Delete');
    this.allowShowLog = this.currentUserService.hasPermission('Maintenance_Action_DocumentRetentions_ShowLog');

    this.documentTypes = await this.developerService.getDocumentTypes();
    await this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading...');
      let retentions: DocumentRetention[] = [];
      retentions = await this. maintenanceService.getDocumentRetentions();
      this.filterAvailableDocumentTypes(retentions);
      this.documentRetentions = retentions;
    }
    catch (err) {
      console.error(err);
      this.toastService.showToast('Failed to load data...', ToastType.error);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  filterAvailableDocumentTypes(arr: DocumentRetention[]): void {
    const dbFilter = arr.map(db => db.DocumentTypeId);
    this.availableDocumentTypes = this.documentTypes.filter(d => !dbFilter.includes(d.Id));
    this.allowAdding = this.availableDocumentTypes.length > 0 && this.currentUserService.hasPermission('Maintenance_Action_DocumentRetentions_Create');
  }

  onInitNewRow(e: any): void {
    e.data = {
      Id: 0,
      DocumentTypeId: null,
      DaysToKeepAdditionalFiles: 0,
      DaysToKeepInterchangeIn: 0,
      DaysToKeepInterchangeOut: 0,
      DaysToKeepCDM: 0,
      DaysToKeepMessageIn: 0,
      DaysToKeepTrackingData: 0,
      DaysToKeepTrackingFields: 0,
      DaysToKeepTransportInLogs: 0,
      DaysToKeepTransportOutLogs: 0,
      DaysToKeepDocumentFlowStatus: 0,
      DaysToKeepBatchMessage: 0,
      IsDefault: false,
    } as DocumentRetention;
    this.availableDocumentTypesForEditing = this.availableDocumentTypes.filter(d => true);
  }

  onRowInserting = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow(e.data as DocumentRetention, item => this.saveDocumentRetention(item));
  };

  onRowUpdating = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as DocumentRetention, item => this.saveDocumentRetention(item), e.newData);
  };

  onRowRemoving = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow(e.data as DocumentRetention, item => this.deleteDocumentRetention(item));
  };

  async saveDocumentRetention(retention: DocumentRetention): Promise<DocumentRetention> {
      const newRetention = await this.maintenanceService.saveDocumentRetention(retention);
      this.toastService.showToast('Document retention saved...', ToastType.success);
      return newRetention;
  }

  async deleteDocumentRetention(retention: DocumentRetention): Promise<DocumentRetention> {
    await this.maintenanceService.deleteDocumentRetention(retention.Id).then( () => {
      this.filterAvailableDocumentTypes(this.documentRetentions);
    });
    this.toastService.showToast('Document retention deleted...', ToastType.success);

    return retention;
  }

  // For use with grid in drop down template
  onSelectionChanged(selectedRowKeys, cellInfo, dropDownBoxComponent): void {
    cellInfo.setValue(selectedRowKeys[0]);
    if (selectedRowKeys.length > 0) {
      dropDownBoxComponent.close();
    }
  }

  onEditingStart(e: any) {
    const a = e.data.DocumentTypeId;
    this.availableDocumentTypesForEditing = this.availableDocumentTypes.filter(d => true);
    this.availableDocumentTypesForEditing.push(this.documentTypes.find(d => d.Id === a));
  }

  validateNumber(e) {
    if (
      e.value > 0 &&
      e.data.DaysToKeepTrackingFields <= e.value &&
      e.data.DaysToKeepInterchangeIn <= e.value &&
      e.data.DaysToKeepMessageIn <= e.value &&
      e.data.DaysToKeepCDM <= e.value &&
      e.data.DaysToKeepInterchangeOut <= e.value &&
      e.data.DaysToKeepTransportOutLogs <= e.value &&
      e.data.DaysToKeepTransportInLogs <= e.value &&
      e.data.DaysToKeepDocumentFlowStatus <= e.value &&
      e.data.DaysToKeepBatchMessage <= e.value &&
      e.data.DaysToKeepAdditionalFiles <= e.value
    ) {
      return true;
    }
    return false;
  }

  getDocumentTypeDisplayValue = rowData => {
    if (rowData.IsDefault)
    {
      return 'DEFAULT';
    }
    else if (rowData.IsUnknown) {
      return 'UNKNOWN';
    }
    else {
      const result = this.documentTypes.find(({ Id }) => Id === rowData.DocumentTypeId);
      return result?.Name;
    }
  };

  getAdditionalFileDisplayValue = rowData => {
    if (rowData.IsDefault === true) {
      return rowData.DaysToKeepAdditionalFiles;
    }
    else {
      return '';
    }
  };

  onCellPrepared(e) {
    if (e.rowType === 'data') {
      if (e.data.IsDefault || e.data.IsUnknown) {
        e.cellElement.find('.dx-link-delete').remove();

        if (e.column.dataField === 'DocumentTypeId') {
          e.cellElement[0].style.setProperty( 'font-weight', 'bold', 'important' );

          on(e.cellElement, 'mouseover', arg => {
            if (e.data.IsDefault) { this.toolTipText = 'If a retention policy is not specified for a document type, values from this row will be used'; }
            if (e.data.IsUnknown) { this.toolTipText = 'This retension policy will be used for documents with an unknown document type'; }
            this.tooltip.instance.show(arg.target);
          });
          on(e.cellElement, 'mouseout', arg => {
            this.tooltip.instance.hide();
          });
        }
      }
    }
  }

  customizeItem = (item: DxiItemComponent) => {
    if (item.dataField === 'DaysToKeepAdditionalFiles' ) {
      const gridInstance: dxDataGrid = this.grid.instance;
      const editing = gridInstance.option('editing');

      let isDefault = false;
      if (editing.changes.length > 0) {
        isDefault = editing.changes[0].data.IsDefault;
      }
      else {
        isDefault = editing.editRowKey.IsDefault;
      }

      item.visible = isDefault;
    }
  };
}
